import { useState, useEffect, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { RefreshCw, FileText, Users, Landmark, Receipt, AlertTriangle } from 'lucide-react';
import { formatDateTime } from '@/lib/date-display.js';
import {
  listPaperDrafts,
  createPaperDraft,
  approvePaperDraft,
  discardPaperDraft,
  paperDraftPdfPath,
} from '@/services/deals.service.js';

// Paperwork drafts review sheet (DEAL-LIFECYCLE-SPEC Phase 2).
// Every bundle generation lands here as a DRAFT: PDF preview + a data
// summary ("2 pilots? Look again before this prints.") + Approve & file /
// Discard. Approve is THE single file step — WorkDrive final folder,
// inbound_documents, transaction link. Nothing reaches client paper
// without the broker's eyes on it first.

function fmtMoney(n) {
  if (n == null || !Number.isFinite(Number(n))) return null;
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2 });
}
const fmtWhen = (iso) => formatDateTime(iso);

const TRIGGER_LABEL = {
  bind: 'Bind completed',
  endorsement: 'Endorsement',
  correction: 'Correction',
  manual: 'Manual regenerate',
};

function SummaryLine({ draft }) {
  const s = draft.data_summary || {};
  const pilots = s.named_pilots || [];
  const balance = (Number(s.total_charges || 0) - Number(s.total_credits || 0));
  return (
    <div className="space-y-1.5 text-[12.5px] text-text-muted">
      <div className="flex items-center gap-1.5">
        <Users size={13} strokeWidth={1.7} className="shrink-0 text-text-dim" />
        <span>
          {pilots.length} pilot{pilots.length === 1 ? '' : 's'}
          {pilots.length ? `: ${pilots.join(', ')}` : ''}
          {(s.excluded_pilots || []).length ? ` · ${s.excluded_pilots.length} excluded` : ''}
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <Landmark size={13} strokeWidth={1.7} className="shrink-0 text-text-dim" />
        <span>
          {s.interest_count || 0} interest{s.interest_count === 1 ? '' : 's'}
          {(s.interest_names || []).length ? `: ${s.interest_names.join(', ')}` : ''}
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <Receipt size={13} strokeWidth={1.7} className="shrink-0 text-text-dim" />
        <span>
          {s.statement_included
            ? `Statement included · charges ${fmtMoney(s.total_charges) || '$0.00'} · credits ${fmtMoney(s.total_credits) || '$0.00'} · balance ${fmtMoney(balance) || '$0.00'}`
            : `No statement${s.statement_skip_reason ? ` (${s.statement_skip_reason})` : ''}`}
        </span>
      </div>
      {(s.names_still_needed || []).length > 0 && (
        <div className="flex items-start gap-1.5 text-warning">
          <AlertTriangle size={13} strokeWidth={1.7} className="mt-0.5 shrink-0" />
          <span>
            {s.names_still_needed.map((line, i) => <span key={i} className="block">{line}</span>)}
            <span className="block">The binder prints a blank there. Set the name in TRACON, then regenerate.</span>
          </span>
        </div>
      )}
      {s.insured_contact_unread && (
        <div className="flex items-start gap-1.5 text-warning">
          <AlertTriangle size={13} strokeWidth={1.7} className="mt-0.5 shrink-0" />
          <span>
            The insured&apos;s phone and email print blank: TRACON could not read the account&apos;s primary contact ({s.insured_contact_unread}). Check the primary contact, then regenerate.
          </span>
        </div>
      )}
      {s.binder_facts?.unread && (
        <div className="flex items-start gap-1.5 text-warning" data-binder-facts-unread>
          <AlertTriangle size={13} strokeWidth={1.7} className="mt-0.5 shrink-0" />
          <span>TRACON could not read the facts this binder prints when it was rendered ({s.binder_facts.unread}), so it cannot tell whether the binder is current. Regenerate before approving.</span>
        </div>
      )}
      {(s.binder_facts?.disagrees || []).length > 0 && (
        <div className="flex items-start gap-1.5 text-warning" data-binder-disagrees>
          <AlertTriangle size={13} strokeWidth={1.7} className="mt-0.5 shrink-0" />
          <span>
            {s.binder_facts.disagrees.map((d) => <span key={d.fact} className="block">{d.words}</span>)}
            <span className="block">The binder prints the CRM&apos;s copy of these. Correct the CRM, then regenerate, before approving.</span>
          </span>
        </div>
      )}
      {(s.limits_missing || []).length > 0 && (
        <div className="flex items-start gap-1.5 text-warning" data-limits-missing>
          <AlertTriangle size={13} strokeWidth={1.7} className="mt-0.5 shrink-0" />
          <span>{s.limits_missing.map((line) => <span key={line} className="block">{line}</span>)}</span>
        </div>
      )}
      {(s.policy_number_missing || []).length > 0 && (
        <div className="flex items-start gap-1.5 text-warning" data-policy-number-missing>
          <AlertTriangle size={13} strokeWidth={1.7} className="mt-0.5 shrink-0" />
          <span>{s.policy_number_missing.map((line) => <span key={line} className="block">{line}</span>)}</span>
        </div>
      )}
      {(s.binder_facts?.crm_copy_disagrees || []).length > 0 && (
        <div className="flex items-start gap-1.5 text-text-dim" data-crm-copy-disagrees>
          <AlertTriangle size={13} strokeWidth={1.7} className="mt-0.5 shrink-0" />
          <span>
            {s.binder_facts.crm_copy_disagrees.map((d) => <span key={d.fact} className="block">{d.words}</span>)}
            <span className="block">The binder prints TRACON&apos;s values; the CRM&apos;s copy is shown only so you can correct it.</span>
          </span>
        </div>
      )}
      {(s.zero_deductible_hull_tails || []).length > 0 && (
        <div className="flex items-start gap-1.5 text-warning">
          <AlertTriangle size={13} strokeWidth={1.7} className="mt-0.5 shrink-0" />
          <span>
            Full Flight hull with $0/$0 deductibles: {s.zero_deductible_hull_tails.join(', ')}. Verify the quoted deductibles before approving.
          </span>
        </div>
      )}
    </div>
  );
}

// focusDraftId: the draft to show first (the one a "Regenerate the binder" press just rendered), else the newest pending.
export default function PaperDraftsSheet({ dealId, open, onOpenChange, onChange, focusDraftId = null }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [active, setActive] = useState(null);        // draft being previewed
  const [previewUrl, setPreviewUrl] = useState(null);
  const [discarding, setDiscarding] = useState(false);
  const [discardReason, setDiscardReason] = useState('');

  const load = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      const res = await listPaperDrafts(dealId);
      const all = res?.drafts || [];
      setDrafts(all);
      const pending = all.filter((d) => d.status === 'pending');
      setActive((prev) => (focusDraftId && pending.find((d) => d.id === focusDraftId)) || pending.find((d) => d.id === prev?.id) || pending[0] || null);
    } catch (err) {
      toast.error(`Couldn't load paperwork drafts: ${err?.body?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [dealId, focusDraftId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Authenticated PDF fetch → object URL for the iframe (COISheet pattern).
  useEffect(() => {
    let alive = true;
    let url = null;
    setPreviewUrl(null);
    if (!active?.id) return undefined;
    (async () => {
      try {
        const token = localStorage.getItem('tracon_token');
        const res = await fetch(paperDraftPdfPath(active.id), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`preview ${res.status}`);
        const blob = await res.blob();
        if (!alive) return;
        url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      } catch (err) {
        if (alive) toast.error(`Preview failed: ${err.message}`);
      }
    })();
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [active?.id]);

  async function handleRegenerate() {
    setBusy(true);
    try {
      await createPaperDraft(dealId);
      toast.success('Fresh draft rendered');
      await load();
      onChange?.();
    } catch (err) {
      toast.error(`Draft render failed: ${err?.body?.error || err.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleApprove() {
    if (!active) return;
    setBusy(true);
    try {
      const res = await approvePaperDraft(active.id);
      toast.success(`Filed: ${res?.fileName || active.file_name}`);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(`Approve failed: ${err?.body?.error || err.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleDiscard() {
    if (!active) return;
    setBusy(true);
    try {
      await discardPaperDraft(active.id, discardReason);
      toast.success('Draft discarded');
      setDiscarding(false);
      setDiscardReason('');
      await load();
      onChange?.();
    } catch (err) {
      toast.error(`Discard failed: ${err?.body?.error || err.message}`);
    } finally {
      setBusy(false);
    }
  }

  const pending = drafts.filter((d) => d.status === 'pending');
  const decided = drafts.filter((d) => d.status !== 'pending').slice(0, 5);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-2xl" data-paper-drafts-sheet data-active-draft={active?.id || ''}>
        <SheetHeader>
          <SheetTitle>Paperwork drafts</SheetTitle>
          <SheetDescription>
            Client paper is preview-approve: review the draft, then Approve &amp; file. Nothing
            reaches the client until you approve it here.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-4 overflow-y-auto py-3">
          <div className="flex items-center justify-between">
            <span className="text-[12px] font-semibold uppercase tracking-wider text-text-dim">
              Pending ({pending.length})
            </span>
            <Button variant="outline" size="sm" disabled={busy} onClick={handleRegenerate} className="gap-1.5">
              <RefreshCw size={13} strokeWidth={1.7} />Regenerate paperwork
            </Button>
          </div>

          {loading ? (
            <p className="text-sm text-text-dim">Loading…</p>
          ) : pending.length === 0 ? (
            <p className="text-sm text-text-dim">
              No drafts waiting. Regenerate to render a fresh bundle from current deal state.
            </p>
          ) : (
            <ul className="space-y-2">
              {pending.map((d) => (
                <li
                  key={d.id}
                  className={`cursor-pointer rounded-md border p-3 ${active?.id === d.id ? 'border-accent bg-surface-strong' : 'border-border bg-surface hover:border-border-strong'}`}
                  onClick={() => setActive(d)}
                >
                  <div className="mb-1.5 flex items-center gap-2">
                    <FileText size={14} strokeWidth={1.7} className="shrink-0 text-text-dim" />
                    <span className="truncate text-[13px] font-medium text-text">{d.file_name}</span>
                    <Badge variant="warning" className="ml-auto shrink-0">
                      {TRIGGER_LABEL[d.trigger] || d.trigger}
                    </Badge>
                  </div>
                  <SummaryLine draft={d} />
                  <p className="mt-1.5 text-[11px] text-text-dim">Rendered {fmtWhen(d.created_at)}</p>
                </li>
              ))}
            </ul>
          )}

          {active ? (
            <div className="space-y-2">
              <span className="text-[12px] font-semibold uppercase tracking-wider text-text-dim">Preview</span>
              {previewUrl ? (
                <iframe title="Draft preview" src={previewUrl} className="h-[420px] w-full rounded-md border border-border bg-white" />
              ) : (
                <div className="grid h-[420px] w-full place-items-center rounded-md border border-border text-sm text-text-dim">
                  Loading preview…
                </div>
              )}
              {discarding ? (
                <div className="space-y-2 rounded-md border border-border p-3">
                  <label className="text-[12px] text-text-muted" htmlFor="discard-reason">Why discard this draft?</label>
                  <input
                    id="discard-reason"
                    value={discardReason}
                    onChange={(e) => setDiscardReason(e.target.value)}
                    placeholder="e.g. roster wrong, regenerating after fix"
                    className="w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text"
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setDiscarding(false)}>Cancel</Button>
                    <Button variant="destructive" size="sm" disabled={busy || !discardReason.trim()} onClick={handleDiscard}>
                      Discard draft
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-end gap-2">
                  <Button variant="outline" disabled={busy} onClick={() => setDiscarding(true)}>Discard</Button>
                  <Button disabled={busy} onClick={handleApprove}>Approve &amp; file</Button>
                </div>
              )}
            </div>
          ) : null}

          {decided.length ? (
            <div className="space-y-1.5">
              <span className="text-[12px] font-semibold uppercase tracking-wider text-text-dim">Recent decisions</span>
              <ul className="space-y-1">
                {decided.map((d) => (
                  <li key={d.id} className="flex items-center gap-2 text-[12px] text-text-dim">
                    <Badge variant={d.status === 'approved' ? 'success' : 'muted'}>{d.status}</Badge>
                    <span className="truncate">{d.file_name}</span>
                    <span className="ml-auto shrink-0">{fmtWhen(d.decided_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
